import dayjs from 'dayjs';
import {
  mapAssignmentToOfferFilterKey,
  resolveOrderDeliveryModel,
  resolveScenarioConfig,
  type OrderOfferStatusKey as OfferStatusFilterKey,
  type ScenarioDriverInput,
  type ScenarioLegInput,
  type ScenarioMoveType,
  type ScenarioOfferInput,
  type ScenarioTooltipContent,
  type ScenarioTooltipNode,
} from '@hopdrive/feature-order-index';
import { isOfferAccepted, resolveDriverAssignment, resolveOfferAuditFields } from '@hopdrive/feature-driver-assignment';
import { formatOfferActor, formatOfferTime } from '@features/moves/utils/moveDetailsAssignment';
import { resolveAssignBlock } from '@hopdrive/feature-driver-assignment';
import type { ViewAssignmentsQuery } from '../queries/viewAssignments.query.generated';

/** One report row — one order, already classified by the shared derivation. */
export interface ViewAssignmentsRow {
  scenarioId: number;
  /** Translated scenario key (e.g. "Valet Pickup"), from the shared config resolution. */
  displayName: string;
  /** Drive move ids in cardinality order — rendered joined, as on the dashboard's Order column. */
  driveMoveIds: number[];
  /** The committed/offered driver, or null when unassigned. */
  driver: ScenarioDriverInput | null;
  /** Unassigned | Offered | Accepted — never null: no-bucket orders are excluded (see below). */
  status: OfferStatusFilterKey;
  offeredAt: string | null;
  offeredBy: string | null;
  acceptedAt: string | null;
  acceptedBy: string | null;
  /** Offered -> accepted span in ms, accepted rows only; null otherwise. */
  timeToAcceptMs: number | null;
  /** Guest name for concierge/loaner scenarios, or null for non-guest-facing orders. */
  consumerName: string | null;
  /**
   * Whether the row's Assign/Reassign action may be offered — the SAME
   * `canAssignOrUnassignMove` verdict the move details panel greys its own button on,
   * evaluated against the move the action would actually dispatch (`driveMoveIds[0]`).
   * A report that offers an action the details page refuses lets a dealer reassign a
   * driver on a delivered move.
   */
  assignable: boolean;
  /** Why not, for the disabled entry's tooltip. Null exactly when `assignable`. */
  assignBlockedReason: string | null;
  /**
   * The newest bundle's live offers (offered / accepted / assigned), newest-first: what
   * the assign modal needs to know someone already holds the order. An offered move has
   * no driver_id until the accept lands, so without these the modal read a Pending
   * Acceptance row as unassigned and showed Assign instead of Reassign / Unassign.
   */
  offers: OfferRow[];
}

export interface ViewAssignmentsReport {
  rows: ViewAssignmentsRow[];
  /** Per-bucket counts over `rows` — they sum to `rows.length`, since no-bucket orders are excluded. */
  counts: Record<OfferStatusFilterKey, number>;
  /**
   * The query hit the move cap: the fetched set (and therefore the rows and counts) is a
   * prefix of the window, not the whole of it. Surfaced prominently so a dealer never
   * reads a KPI as a window-total when it is a cap-total.
   */
  truncated: boolean;
}

type MoveRow = NonNullable<ViewAssignmentsQuery['moves'][number]>;
type ScenarioNode = NonNullable<MoveRow['scenariotomoves'][number]['scenario']>;
type OfferRow = ScenarioNode['bundletoscenarios'][number]['bundle']['offers'][number];

const toMoveType = (moveType: string | null | undefined): ScenarioMoveType => (moveType === 'ride' ? 'ride' : 'drive');

/**
 * The order's legs, excluding inactive members (`active === 0`) — the same exclusion the
 * board's `buildLegList` makes, so the derivation sees the identical leg set the pill does.
 * Fields the derivation never reads are null: these legs exist to be classified, not rendered.
 */
const toLegInputs = (scenario: ScenarioNode): ScenarioLegInput[] =>
  (scenario.moves ?? [])
    .filter(member => member?.move && member.move.active !== 0)
    .map(member => ({
      id: member.id,
      cardinality: member.cardinality,
      move: {
        id: member.move.id,
        move_type: toMoveType(member.move.move_type),
        active: member.move.active,
        status: member.move.status ?? null,
        cancel_status: null,
        ready_by: null,
        target_pickup_time: null,
        lite: member.move.lite ?? null,
        driver_id: member.move.driver_id ?? null,
        driver_name: member.move.driver_name ?? null,
      },
    }));

/**
 * The row's assignability, and why not.
 *
 * `canAssignOrUnassignMove` is the shared gate the details panel uses; it wants the move's
 * own `lite`/`status`, its PARENT's status, and its active ride children. All three are
 * resolvable from the scenario's own legs via `parent_move_id`, so the report reaches the
 * same verdict without a second round trip. A parent outside this scenario resolves to
 * null, which the gate treats as "no parent" — the same thing it does when the details
 * page has no parent to read.
 *
 * The reason comes from the shared gate's own verdict (resolveAssignBlock), mapped to text
 * here. Each names the offending status rather than narrating a lifecycle, so the text
 * stays true if the status set grows.
 */
const resolveAssignability = (
  scenario: ScenarioNode,
  targetMoveId: number
): { assignable: boolean; assignBlockedReason: string | null } => {
  const members = (scenario.moves ?? []).filter(member => member?.move);
  const target = members.find(member => member.move.id === targetMoveId)?.move;
  // No target move in the scenario should be impossible (the id came from these legs), but
  // refusing is the safe answer: never offer a dispatch against a move we cannot inspect.
  if (!target) return { assignable: false, assignBlockedReason: 'This order could not be read for assignment.' };

  const parent = target.parent_move_id
    ? (members.find(member => member.move.id === target.parent_move_id)?.move ?? null)
    : null;
  const childMoves = members
    .filter(member => member.move.parent_move_id === target.id)
    .map(member => ({
      move_type: member.move.move_type ?? '',
      active: member.move.active ?? null,
      status: member.move.status ?? null,
    }));

  const input = {
    lite: target.lite ?? null,
    status: target.status ?? null,
    parentMove: parent ? { status: parent.status ?? null } : null,
    childMoves,
  };

  // The shared gate names the clause it failed on, so the tooltip can't drift from it.
  const block = resolveAssignBlock(input);
  if (!block) return { assignable: true, assignBlockedReason: null };
  switch (block.code) {
    case 'not_lite':
      return { assignable: false, assignBlockedReason: 'Full Service orders are dispatched by HopDrive, not from the portal.' };
    case 'move_status':
      return { assignable: false, assignBlockedReason: `This move is ${block.status ?? 'in an unknown state'} — a driver can only be changed before pickup completes.` };
    case 'parent_status':
      return { assignable: false, assignBlockedReason: `The parent move is ${block.status ?? 'in an unknown state'} — a driver can only be changed before pickup completes.` };
    case 'ride_status':
      return { assignable: false, assignBlockedReason: `This order's rideshare is ${block.status} — it can no longer be retracted.` };
    default:
      return { assignable: false, assignBlockedReason: 'This move can no longer be assigned.' };
  }
};

/** The newest bundle's offers (first non-empty), newest-first — same rule as the board's toOfferInputs. */
const pickNewestOffers = (scenario: ScenarioNode): OfferRow[] => {
  for (const link of scenario.bundletoscenarios ?? []) {
    const offers = link.bundle?.offers ?? [];
    if (offers.length) return offers;
  }
  return [];
};

const toOfferInput = (offer: OfferRow): ScenarioOfferInput => ({
  id: offer.id,
  status: offer.status ?? null,
  driver: offer.driver
    ? { id: offer.driver.id, display_name: offer.driver.user?.display_name ?? null, avatar_url: offer.driver.user?.avatar_url ?? null }
    : null,
  offered_at: offer.offered_at ?? null,
  offered_by: offer.offered_by ?? null,
  accepted_at: offer.accepted_at ?? null,
  accepted_by: offer.accepted_by ?? null,
  createdat: offer.createdat ?? null,
  createdby: offer.createdby ?? null,
  updatedat: offer.updatedat ?? null,
  updatedby: offer.updatedby ?? null,
});

/**
 * One order -> one report row, or `null` when the order is out of scope for an OFFER report.
 *
 * SCOPE (deliberate, not a query hole): this report is the offer lifecycle. An order that
 * the shared derivation classifies as `locked` with NO committed driver — Full Service
 * (never dealer-assignable), and completed/cancelled orders that never took a driver —
 * maps to no offer bucket (`mapAssignmentToOfferFilterKey` returns null) and is excluded
 * here, so the three KPI tiles sum exactly to the visible rows. The orders are still
 * fetched by the window query; they are dropped at THIS classification step by design, so
 * "where are my Full Service orders" has the answer "the offer report scopes to
 * offer-lifecycle orders", not "they fell out of the query somewhere". Same distinction as
 * the 4A filter and the 4B panel.
 */
const buildRow = (scenario: ScenarioNode, rooftopTier: string | null): ViewAssignmentsRow | null => {
  const legs = toLegInputs(scenario);
  const driveLegs = legs.filter(leg => leg.move.move_type === 'drive');
  // No drive legs → no row, by scope: the offer lifecycle this report covers hangs
  // off drive moves (rides are children dispatched alongside a drive). Today the
  // window query is rooted at drive moves, so a ride-only scenario can't even reach
  // here — this guard is for a future scenario shape, and if one ever carries its
  // own assignable work, this exclusion (like the locked-no-driver one below) must
  // become a deliberate decision, not an accident of the filter.
  if (!driveLegs.length) return null;

  const offers = pickNewestOffers(scenario);
  const assignment = resolveDriverAssignment({ legs, offers: offers.map(toOfferInput) });
  const status = mapAssignmentToOfferFilterKey(assignment);
  if (!status) return null;

  // The board's config reads the rooftop by operational model; a lite rooftop is the self-managed one.
  const deliveryModel = resolveOrderDeliveryModel({
    rooftopOperationalModel: rooftopTier === 'lite' ? 'self-managed' : 'hopdrive-managed',
    firstDriveLegIsLite: driveLegs[0]?.move.lite ?? null,
  });
  const config = resolveScenarioConfig({ scenarioKey: scenario.scenario_key, deliveryModel });

  // The offer the verdict keys on — the ACCEPTED offer when one exists, else the newest.
  // Same rule as resolveDriverAssignment above, so the Status column and the
  // Offered/Accepted columns can never describe different offers: in a broadcast the
  // newest-created offer may still be pending while an older one was taken.
  const winning = offers.find(isOfferAccepted) ?? offers[0] ?? null;

  const isAccepted = status === 'accepted';
  const { offeredAt, offeredBy, acceptedAt, acceptedBy } = resolveOfferAuditFields(winning);

  // A negative span means the audit pair is incoherent (e.g. accepted_at stamped
  // against a different offered fallback source) — report "unknown" (null, renders
  // as a dash and sorts with the other unknowns) rather than a surprising negative
  // or a fake instant 0.
  const rawTimeToAccept = isAccepted && offeredAt && acceptedAt ? dayjs(acceptedAt).diff(dayjs(offeredAt)) : null;
  const timeToAcceptMs = rawTimeToAccept != null && rawTimeToAccept < 0 ? null : rawTimeToAccept;

  // Guest name from any drive leg's move that has one (concierge/loaner scenarios)
  const consumerName =
    (scenario.moves ?? []).find(member => member.move.move_type === 'drive' && member.move.consumer_name)?.move
      .consumer_name ?? null;

  // Gate the move the action actually dispatches against — driveMoveIds[0], the same one
  // handleAssignDriver fetches and handleViewDetails navigates to.
  const driveMoveIds = driveLegs.map(leg => leg.move.id);
  const { assignable, assignBlockedReason } = resolveAssignability(scenario, driveMoveIds[0]);

  return {
    scenarioId: scenario.id,
    displayName: config?.displayName ?? scenario.scenario_key,
    driveMoveIds,
    driver: assignment.driver,
    status,
    offeredAt,
    offeredBy,
    acceptedAt,
    acceptedBy,
    timeToAcceptMs,
    consumerName,
    assignable,
    assignBlockedReason,
    offers,
  };
};

/**
 * Query result -> report. De-duplicates the moves-rooted result to one row per order,
 * classifies each with the SAME `resolveDriverAssignment` the board uses, and drops the
 * truncation-probe row (surfacing `truncated`).
 */
export const buildViewAssignmentsReport = (
  data: ViewAssignmentsQuery | null | undefined,
  { rooftopTier, cap }: { rooftopTier: string | null; cap: number }
): ViewAssignmentsReport => {
  const moves = data?.moves ?? [];
  const truncated = moves.length > cap;
  const capped = truncated ? moves.slice(0, cap) : moves;

  // A scenario reached through several of its drive moves appears once.
  const scenariosById = new Map<number, ScenarioNode>();
  for (const move of capped) {
    const scenario = move.scenariotomoves[0]?.scenario;
    if (scenario && !scenariosById.has(scenario.id)) scenariosById.set(scenario.id, scenario);
  }

  const rows: ViewAssignmentsRow[] = [];
  for (const scenario of scenariosById.values()) {
    const row = buildRow(scenario, rooftopTier);
    if (row) rows.push(row);
  }

  const counts: Record<OfferStatusFilterKey, number> = { unassigned: 0, offered: 0, accepted: 0 };
  for (const row of rows) counts[row.status] += 1;

  return { rows, counts, truncated };
};

/**
 * Offered -> accepted span for the Time to accept column, day-rolled and legible at a glance
 * — a dispute surface favors "3d 1h" over "73h 12m". Non-accepted rows (null) render as a dash.
 */
export const formatTimeToAccept = (ms: number | null): string => {
  if (ms == null || ms < 0) return '—';
  const totalSeconds = Math.round(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

// ── Status tooltip ───────────────────────────────────────────────────────────

const tooltipTitle = (text: string): ScenarioTooltipNode => ({ kind: 'title', text });
const tooltipRow = (key: string, value: string): ScenarioTooltipNode => ({ kind: 'row', key, value });
const tooltipDescription = (text: string): ScenarioTooltipNode => ({ kind: 'description', text });

/**
 * Tooltip content for the status chip — shows the driver assignment state with timestamps
 * and actors when available. Read-only display (no action hints).
 */
export const buildStatusTooltip = (assignmentRow: ViewAssignmentsRow): ScenarioTooltipContent => {
  const driverName = assignmentRow.driver?.display_name ?? null;
  const content: ScenarioTooltipContent = [];

  switch (assignmentRow.status) {
    case 'unassigned':
      content.push(tooltipTitle('Unassigned'));
      content.push(tooltipDescription('No driver has been assigned to this order.'));
      break;

    case 'offered':
      content.push(tooltipTitle(driverName ? `Offered to ${driverName}` : 'Offered to driver'));
      if (assignmentRow.offeredAt) {
        content.push(tooltipRow('Offered', formatOfferTime(assignmentRow.offeredAt)));
        const actor = formatOfferActor(assignmentRow.offeredBy);
        if (actor !== '—') content.push(tooltipDescription(`by ${actor}`));
      }
      break;

    case 'accepted': {
      content.push(tooltipTitle(driverName ? `Accepted by ${driverName}` : 'Accepted by driver'));
      if (assignmentRow.offeredAt) content.push(tooltipRow('Offered', formatOfferTime(assignmentRow.offeredAt)));
      if (assignmentRow.acceptedAt) content.push(tooltipRow('Accepted', formatOfferTime(assignmentRow.acceptedAt)));
      const acceptedActor = formatOfferActor(assignmentRow.acceptedBy);
      if (acceptedActor !== '—') content.push(tooltipDescription(`accepted by ${acceptedActor}`));
      break;
    }
  }

  return content;
};
